// Multi-device sync for user preferences.
//
// Server keeps a per-user JSONB blob at GET/PUT /v1/prefs. The SPA
// owns the schema below. On boot the SPA fetches the blob once; the
// stores hydrate from it (overwriting their localStorage default if
// the server's value is newer / non-empty); subsequent local edits
// debounce a PUT back to the server.
//
// Why JSONB blob (not normalized tables): the prefs are user-facing
// config (setpoints, view choices, pinned sensors) — the SPA can
// evolve the shape independently. A typed table would force every
// schema change through Go + DB migrations + an API contract pass.
// The blob accepts whatever JSON the SPA sends; backend doesn't read
// the contents.

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '@/apiConfig';
import { useAuthStore } from '@/store/authStore';
import { useSetpointStore } from '@/store/setpointStore';
import { useViewStore } from '@/store/viewStore';

const PREFS_QUERY_KEY = ['user-prefs'] as const;
const SAVE_DEBOUNCE_MS = 800;

/** Shape of the prefs blob. Add new keys as new domains move to
 *  server-side persistence. Optional everywhere so a partial blob
 *  from an older client doesn't blow up newer code. */
export interface UserPrefsBlob {
  /** Setpoints — keyed by setpoint id. Mirrors useSetpointStore.setpoints. */
  setpoints?:        Record<string, unknown>;
  /** Selected machines (most recent dashboard selection). */
  selectedMachines?: string[];
  /** Linked pins (shared across machines in linked mode). */
  linkedPins?:       string[];
  /** Independent pins (per-machine in independent mode). */
  independentPins?:  Record<string, string[]>;
  /** View mode toggle ('linked' | 'independent'). */
  viewMode?:         string;
  /** Free-form catch-all for future expansion. */
  [key: string]:     unknown;
}

interface PrefsResponse {
  prefs: UserPrefsBlob | null;
}

/** Fetch the server-side prefs blob once on auth. */
export function useUserPrefsQuery() {
  const token = useAuthStore((s) => s.user?.token ?? null);
  return useQuery({
    queryKey: PREFS_QUERY_KEY,
    enabled:  !!token,
    staleTime: 5 * 60_000, // 5 min — local edits push via mutation; refetches catch other-device updates
    queryFn: async (): Promise<PrefsResponse> => {
      const res = await fetch(`${API_BASE}/prefs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`prefs ${res.status}`);
      return res.json();
    },
  });
}

/** Push a fresh prefs blob to the server. Idempotent (PUT). */
export function useSaveUserPrefs() {
  const token = useAuthStore((s) => s.user?.token ?? null);
  const qc    = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: UserPrefsBlob) => {
      if (!token) throw new Error('not authenticated');
      const res = await fetch(`${API_BASE}/prefs`, {
        method:  'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ prefs }),
      });
      if (!res.ok) throw new Error(`prefs save ${res.status}`);
    },
    onSuccess: (_, prefs) => {
      // Update the cached query so other consumers don't see stale data.
      qc.setQueryData<PrefsResponse>(PREFS_QUERY_KEY, { prefs });
    },
  });
}

/** Build the current prefs blob from the live stores. Called when
 *  we decide to push. */
function snapshotFromStores(): UserPrefsBlob {
  const sp = useSetpointStore.getState().setpoints;
  const vs = useViewStore.getState();
  return {
    setpoints:        sp,
    selectedMachines: vs.selectedMachines,
    linkedPins:       vs.linkedPins,
    independentPins:  vs.independentPins,
    viewMode:         vs.viewMode,
  };
}

/** Hydrate stores from the server blob. Only fills fields the blob
 *  actually contains — undefined keys leave the store's current
 *  value alone. */
function hydrateStores(blob: UserPrefsBlob): void {
  if (blob.setpoints && typeof blob.setpoints === 'object') {
    // Replace the whole map; setpointStore manages identity.
    useSetpointStore.setState({ setpoints: blob.setpoints as Record<string, never> });
  }
  if (Array.isArray(blob.selectedMachines)) {
    useViewStore.getState().setMachines(blob.selectedMachines);
  }
  if (Array.isArray(blob.linkedPins)) {
    useViewStore.setState({ linkedPins: blob.linkedPins });
  }
  if (blob.independentPins && typeof blob.independentPins === 'object') {
    useViewStore.setState({ independentPins: blob.independentPins as Record<string, string[]> });
  }
  if (blob.viewMode === 'linked' || blob.viewMode === 'independent') {
    useViewStore.setState({ viewMode: blob.viewMode });
  }
}

/** Mount this once at the auth-gated shell level. It:
 *    1. Fetches server prefs on auth
 *    2. Hydrates the stores from the result (once)
 *    3. Subscribes to store changes and debounce-saves them back
 *
 *  Local-only behaviour is unchanged when offline — the stores still
 *  persist to localStorage; this just adds a cloud mirror. */
export function useUserPrefsSync(): void {
  const { data } = useUserPrefsQuery();
  const save = useSaveUserPrefs();
  const hydratedRef = useRef<boolean>(false);
  const saveRef     = useRef(save);
  saveRef.current   = save;

  // Hydrate once when the prefs land.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!data) return;
    const blob = data.prefs;
    if (!blob || Object.keys(blob).length === 0) {
      // First-time user: server is empty. Push the current local
      // state up so this device's localStorage seeds the cloud. The
      // OTHER devices will then hydrate from there on next load.
      const snap = snapshotFromStores();
      if (Object.keys(snap.setpoints ?? {}).length > 0
          || (snap.selectedMachines?.length ?? 0) > 0
          || (snap.linkedPins?.length ?? 0) > 0) {
        saveRef.current.mutate(snap);
      }
    } else {
      hydrateStores(blob);
    }
    hydratedRef.current = true;
  }, [data]);

  // Debounced save on store changes. Subscribe to each store; on any
  // change, schedule a single save with the LATEST snapshot.
  useEffect(() => {
    if (!hydratedRef.current) return; // don't save before initial hydrate
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        saveRef.current.mutate(snapshotFromStores());
      }, SAVE_DEBOUNCE_MS);
    };
    const unsub1 = useSetpointStore.subscribe(schedule);
    const unsub2 = useViewStore.subscribe(schedule);
    return () => {
      if (timer) clearTimeout(timer);
      unsub1();
      unsub2();
    };
  }, [data]);
}
